import { DOCS_BASE_URL } from '$lib/services/app';

/**
 * Mapping of deprecation warnings that have been issued once. This prevents flooding the console
 * with repeated warnings.
 * @type {Record<string, boolean>}
 */
export const warnedOnceMap = {
  slug_length: false,
  slug_editor_tag: false,
  yaml_quote: false,
  save_all_locales: false,
  automatic_deployments: false,
  multiple_folders_i18n_root: false,
  omit_default_locale_from_filename: false,
  logo_url: false,
};

/**
 * Deprecation warning messages.
 * @type {Record<string, string>}
 */
export const warningMessages = {
  slug_length:
    'The `slug_length` collection option is deprecated and will be removed in Sveltia CMS 1.0. ' +
    'Use the global `slug.maxlength` option instead. ',
  slug_editor_tag:
    'The `{{fields._slug}}` and `{{fields._slug | localize}}` slug template tags are deprecated ' +
    'and will be removed in Sveltia CMS 1.0. Use the `slug` collection option’s object form ' +
    'instead: `{{fields._slug}}` is equivalent to `slug: { editable: true }`, and ' +
    '`{{fields._slug | localize}}` is equivalent to `slug: { editable: true, i18n: true }`. ' +
    `${DOCS_BASE_URL}/collections/entries/slugs#making-slugs-editable`,
  yaml_quote:
    'The `yaml_quote` collection option is deprecated and will be removed in Sveltia CMS 1.0. ' +
    'Use the global `output.yaml.quote` option instead. `yaml_quote: true` is equivalent to ' +
    '`quote: double`. ' +
    `${DOCS_BASE_URL}/data-output#controlling-data-output`,
  save_all_locales:
    'The `save_all_locales` i18n option is deprecated and will be removed in Sveltia CMS 1.0. ' +
    'Use the `initial_locales` option instead. `save_all_locales: false` is equivalent to ' +
    '`initial_locales: all`. ' +
    `${DOCS_BASE_URL}/i18n/options#disabling-non-default-locale-content`,
  automatic_deployments:
    'The `automatic_deployments` backend option is deprecated and will be removed in Sveltia CMS ' +
    '1.0. Use the `skip_ci` option instead. `automatic_deployments: false` is equivalent to ' +
    '`skip_ci: true`, and `automatic_deployments: true` is equivalent to `skip_ci: false`. ' +
    `${DOCS_BASE_URL}/deployments#disabling-automatic-deployments`,
  multiple_folders_i18n_root:
    'The `multiple_folders_i18n_root` i18n structure is deprecated and will be removed in ' +
    'Sveltia CMS 1.0. Use the `multiple_root_folders` structure instead. ' +
    `${DOCS_BASE_URL}/i18n/structures#multiple-root-folders`,
  omit_default_locale_from_filename:
    'The `omit_default_locale_from_filename` i18n option is deprecated and will be removed in ' +
    'Sveltia CMS 1.0. Use the `omit_default_locale_from_file_path` option instead. ' +
    `${DOCS_BASE_URL}/i18n/structures`,
  logo_url:
    'The `logo_url` option is deprecated and will be removed in a future version of Sveltia CMS. ' +
    'Use the `logo.src` option instead. ' +
    `${DOCS_BASE_URL}/customization#custom-logo`,
};

/**
 * Issue a deprecation warning if it hasn’t been issued yet.
 * @param {string} key Key of the warning to issue.
 * @param {string} [message] Custom message to display instead of the default one.
 */
export const warnDeprecation = (key, message) => {
  // Skip during tests
  if (import.meta.env.VITEST) {
    return;
  }

  if (!warnedOnceMap[key]) {
    // eslint-disable-next-line no-console
    console.warn(message ?? warningMessages[key]);
    warnedOnceMap[key] = true;
  }
};
